import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react'; // ADDED THIS
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube'; // RENAMED for consistency
import { Play } from "lucide-react";
import {
   // replace Youtube
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Image as ImageIcon,
  FileText,
  ExternalLink,
  X,
  Download,
} from "lucide-react";

import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
interface TipTapEditorProps {
  content: string;
  onChange: (content: string) => void;
  onMediaAdd?: (media: any) => void;
}
  const extractMediaFromHTML = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const images = Array.from(doc.querySelectorAll("img")).map((img) => ({
      url: img.getAttribute("src") || "",
    }));

    const pdfs = Array.from(doc.querySelectorAll("a"))
      .filter((a) => a.href.includes(".pdf"))
      .map((a) => ({
        url: a.href,
        name: a.textContent || "PDF",
      }));

    const videos = Array.from(doc.querySelectorAll("iframe")).map((iframe) => ({
      url: iframe.getAttribute("src") || "",
    }));

    return { images, pdfs, videos };
  };   

export function TipTapEditor({ content, onChange, onMediaAdd, }: TipTapEditorProps) {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showYoutubeDialog, setShowYoutubeDialog] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [showExternalLinkDialog, setShowExternalLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfName, setPdfName] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadedVideos, setUploadedVideos] = useState<
    Array<{ url: string; name: string }>
  >([]);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [uploadedPdfs, setUploadedPdfs] = useState<Array<{ name: string; url: string }>>([]);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [embeddedVideos, setEmbeddedVideos] = useState<Array<{ url: string; id?: string }>>([]);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'mb-1 leading-relaxed text-gray-800',
          },
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image,
      Youtube.configure({
        controls: true,
        nocookie: true,
        width: 300,
        height: 170,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });
    <style>
  {`
    .ProseMirror {
    outline: none;
    line-height: 1.4; /* Slightly more breathable than 1.0, but still tight */
    color: #111;
  }

  .ProseMirror p {
    margin-top: 0 !important;
    margin-bottom: 8px !important; /* Adjust this number to control the gap between paragraphs */
  }

  /* This removes the extra gap you added here previously */
  .ProseMirror p + p {
    margin-top: 0px !important; 
  }

    .ProseMirror h1 {
      font-size: 0.5rem;
      font-weight: 600;
      margin: 16px 0;
    }

    .ProseMirror h2 {
      font-size: 1.0rem;
      font-weight: 600;
      margin: 14px 0;
    }

    .ProseMirror img {
      margin: 16px 0;
      border-radius: 8px;
      max-width: 100%;
    }

    .ProseMirror iframe {
      margin: 16px 0;
      border-radius: 8px;
      width: 100%;
    }
  `}
  </style>

  interface UploadedImage {
    url: string;
    file?: File;
    width?: number;
    height?: number;
    sizeKB?: number;
  }
  interface UploadedPdf {
    name: string;
    url: string;
  }
     
  useEffect(() => {
    if (!content) return;

    const { images, pdfs, videos } = extractMediaFromHTML(content);

    setUploadedImages(images);
    setUploadedPdfs(pdfs);
    setEmbeddedVideos(videos);
  }, [content]);

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkDialog(false);
    }
  };

  const addImage = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      // 1. Upload to your backend
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");

      const { url } = await res.json();

      // 2. Insert the permanent URL into TipTap
      editor.chain().focus().setImage({
        src: url,
        alt: file.name,
      }).run();

      // 3. Update local state for the "Attached Media" preview
      const newImage = { 
        url, 
        name: file.name, 
        sizeKB: Math.round(file.size / 1024) 
      };
      
      setUploadedImages((prev) => [...prev, newImage]);

      // 4. Notify CourseBuilder so it can save the media reference
      onMediaAdd?.({
        type: 'image',
        ...newImage
      });

    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image.");
    }
  };
  const addYoutube = () => {
    if (!youtubeUrl) return;

    // ✅ normalize URL (prevents weird embeds)
    const cleanUrl = youtubeUrl.trim();

    editor
      .chain()
      .focus()
      .setYoutubeVideo({
        src: cleanUrl,
        width: 300,
        height: 170,
      })
      .run();

    setEmbeddedVideos((prev) => [...prev, { url: cleanUrl }]);

    onMediaAdd?.({
      type: 'youtube',
      url: cleanUrl,
    });

    setYoutubeUrl('');
    setShowYoutubeDialog(false);
  };

  const handlePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfName(file.name);
    }
  };

  const addPdf = async () => {
    if (!pdfFile || !editor) return;

    try {
      const formData = new FormData();
      formData.append("file", pdfFile);

      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");

      const { url } = await res.json();

      // ✅ Insert PDF preview + download link
      editor.chain().focus().insertContent(`
        <div style="margin: 16px 0;">
          <p style="font-weight: 500; margin-bottom: 8px;">
            📄 ${pdfFile.name}
          </p>

          <iframe
            src="${url}"
            width="100%"
            height="500px"
            style="border: 1px solid #ddd; border-radius: 8px;"
          ></iframe>

          <p style="margin-top: 8px;">
            <a href="${url}" target="_blank" rel="noopener noreferrer">
              ⬇ Download PDF
            </a>
          </p>
        </div>
      `).run();

      // ✅ Update preview state
      setUploadedPdfs((prev) => [
        ...prev,
        { name: pdfFile.name, url }
      ]);

      // ✅ Notify parent
      onMediaAdd?.({
        type: 'pdf',
        name: pdfFile.name,
        url
      });

      // ✅ Reset state
      setPdfFile(null);
      setPdfName('');
      setShowPdfDialog(false);

    } catch (err) {
      console.error("PDF upload failed", err);
    }
  };
  const addVideo = async () => {
    if (!videoFile || !editor) return;

    try {
      const formData = new FormData();
      formData.append("file", videoFile);

      const res = await fetch("/api/upload-video", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");

      const { url } = await res.json();

      // Insert video into editor
      editor.chain().focus().insertContent(`
        <video controls style="width:100%; margin:16px 0; border-radius:8px;">
          <source src="${url}" />
        </video>
      `).run();

      setUploadedVideos((prev) => [
        ...prev,
        { url, name: videoFile.name },
      ]);

      onMediaAdd?.({
        type: "video",
        url,
      });

      setVideoFile(null);
      setShowVideoDialog(false);
    } catch (err) {
      console.error("Video upload failed", err);
    }
  };
  const removeImage = (url: string) => {
    if (!editor) return;

    editor.chain().focus().command(({ tr, state }) => {
      state.doc.descendants((node, pos) => {
        if (node.type.name === "image" && node.attrs.src === url) {
          tr.delete(pos, pos + node.nodeSize);
        }
      });
      return true;
    }).run();
  };
  const removePdf = (url: string) => {
    if (!editor) return;

    editor.chain().focus().command(({ tr, state }) => {
      state.doc.descendants((node, pos) => {
        if (node.type.name === "text" && node.text?.includes(url)) {
          tr.delete(pos, pos + node.nodeSize);
        }
      });
      return true;
    }).run();
  };

  const removeVideo = (url: string) => {
    if (!editor) return;

    editor.chain().focus().command(({ tr, state }) => {
      state.doc.descendants((node, pos) => {
        if (node.type.name === "youtube" && node.attrs.src === url) {
          tr.delete(pos, pos + node.nodeSize);
        }
      });
      return true;
    }).run();
  };
   
  const ToolbarButton = ({ onClick, active, children }: any) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onClick}
      className={`h-8 w-8 ${active ? 'bg-gray-200' : ''}`}
    >
      {children}
    </Button>
  );

 return (
  <div className="border border-gray-300 rounded-xl overflow-hidden shadow-sm bg-white">
    {/* Toolbar */}
    <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
      >
        <UnderlineIcon className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
      >
        <Strikethrough className="w-4 h-4" />
      </ToolbarButton>

      <div className="w-px h-8 bg-gray-300 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive('heading', { level: 1 })}
      >
        <Heading1 className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
      >
        <Heading2 className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
      >
        <Heading3 className="w-4 h-4" />
      </ToolbarButton>

      <div className="w-px h-8 bg-gray-300 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
      >
        <List className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>

      <div className="w-px h-8 bg-gray-300 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
      >
        <AlignLeft className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
      >
        <AlignCenter className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })}
      >
        <AlignRight className="w-4 h-4" />
      </ToolbarButton>

      <div className="w-px h-8 bg-gray-300 mx-1" />

      <ToolbarButton onClick={() => setShowLinkDialog(true)}>
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton onClick={() => setShowImageDialog(true)}>
        <ImageIcon className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton onClick={() => setShowPdfDialog(true)}>
        <FileText className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton onClick={() => setShowYoutubeDialog(true)}>
        <Play className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton onClick={() => setShowVideoDialog(true)}>
        <Play className="w-4 h-4" />
      </ToolbarButton>
    </div>

    {/* Editor Container */}
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 border-b bg-gray-50">
        <p className="text-sm font-medium text-gray-700">
          Chapter Content
        </p>
        <p className="text-xs text-gray-500">
          Write text, add images, videos, PDFs, and links
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 p-3 border-b bg-white flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            editor?.chain().focus().insertContent('<h2>New Section</h2><p></p>').run()
          }
        >
          + Section
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            editor?.chain().focus().insertContent('<p>New paragraph...</p>').run()
          }
        >
          + Paragraph
        </Button>
      </div>

      {/* Actual Editor */}
      <div className="p-4 min-h-[350px] bg-white">
        <EditorContent
          editor={editor}
          className="focus:outline-none text-black"
        />
      </div>
    </div>

    {/* Media Preview Section */}
    {(uploadedPdfs.length > 0 || uploadedImages.length > 0 || uploadedVideos.length > 0) && (
      <div className="border-t border-gray-300 p-4 bg-gray-50">
        <h4 className="font-medium text-sm mb-3">Attached Media</h4>
        
        {/* PDFs */}
        {uploadedPdfs.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">PDFs:</p>
            <div className="space-y-2">
              {uploadedPdfs.map((pdf, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded"
                >
                  <FileText className="w-4 h-4 text-red-500" />
                  <a
                    href={pdf.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm flex-1 truncate hover:underline"
                  >
                    {pdf.name}
                  </a>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => window.open(pdf.url, "_blank")}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => removePdf(pdf.url)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Images */}
        {uploadedImages.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">Images:</p>
            <div className="grid grid-cols-4 gap-2">
              {uploadedImages.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-20 object-cover rounded border"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 bg-white opacity-0 group-hover:opacity-100"
                    onClick={() => removeImage(img.url)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Embedded Videos */}
        {embeddedVideos.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">Embedded Videos:</p>
            <div className="space-y-2">
              {embeddedVideos.map((video, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded"
                >
                  <Play className="w-4 h-4 text-red-500" />
                  <span className="text-sm flex-1 truncate">
                    {video.url}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => removeVideo(video.url)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video Upload Preview */}
        {uploadedVideos?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">Videos:</p>
            <div className="space-y-2">
              {uploadedVideos.map((vid, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2 bg-white border rounded"
                >
                  <Play className="w-4 h-4 text-red-500" />
                  <span className="text-sm flex-1 truncate">
                    {vid.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}

    {/* Dialogs */}
    <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Video</DialogTitle>
        </DialogHeader>
        <Input
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) setVideoFile(file);
          }}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowVideoDialog(false)}>
            Cancel
          </Button>
          <Button onClick={addVideo} disabled={!videoFile}>
            Upload Video
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Link</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Enter URL"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
            Cancel
          </Button>
          <Button onClick={addLink}>Add Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Image</DialogTitle>
        </DialogHeader>
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              addImage(file);
              setShowImageDialog(false);
            }
          }}
        />
        <Input
          placeholder="Or enter image URL"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowImageDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!imageUrl) return;
              const fixedWidth = 700;
              const fixedHeight = 300;
              editor.chain().focus().setImage({
                src: imageUrl,
                width: fixedWidth,
                height: fixedHeight,
              }).run();
              setUploadedImages((prev) => [
                ...prev,
                { url: imageUrl, width: fixedWidth, height: fixedHeight },
              ]);
              onMediaAdd?.({
                type: "image",
                url: imageUrl,
                width: fixedWidth,
                height: fixedHeight,
              });
              setImageUrl("");
              setShowImageDialog(false);
            }}
          >
            Add Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload PDF</DialogTitle>
        </DialogHeader>
        <Input type="file" accept=".pdf" onChange={handlePdfUpload} />
        {pdfName && (
          <p className="text-sm text-gray-600">Selected: {pdfName}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowPdfDialog(false)}>
            Cancel
          </Button>
          <Button onClick={addPdf} disabled={!pdfFile}>
            Upload PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
);
}